import React, { useState } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const SECTIONS = [
  { key: "overview",          label: "Overview" },
  { key: "platforms",         label: "Platforms" },
  { key: "social_media",      label: "Social Media" },
  { key: "audience",          label: "Audience" },
  { key: "demographics",      label: "Demographics" },
  { key: "technology_devices",label: "Technology & Devices" },
  { key: "trending",          label: "Trending" },
];

const ROLES = [
  { value: "viewer",      label: "Viewer",      desc: "Access only to granted sections" },
  { value: "editor",      label: "Editor",      desc: "Can view all sections, cannot manage users" },
  { value: "admin",       label: "Admin",       desc: "Full access, can manage non-super users" },
];

function allTrue()  { return Object.fromEntries(SECTIONS.map(s => [s.key, true])); }
function allFalse() { return Object.fromEntries(SECTIONS.map(s => [s.key, false])); }

export function AddUserForm({ currentUser, isSuperAdmin, theme: t, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name: "", email: "", role: "viewer",
    permissions: allFalse(),
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");
  const [success, setSuccess] = useState("");

  // When role changes, update default permissions
  function handleRoleChange(role) {
    const perms = (role === "viewer") ? allFalse() : allTrue();
    setForm(f => ({ ...f, role, permissions: perms }));
  }

  function togglePerm(key) {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(""); setSuccess("");
    if (!form.name.trim()) { setErr("Full name is required"); return; }
    if (!form.email.trim()) { setErr("Email is required"); return; }
    if (!form.email.toLowerCase().endsWith("@alphamedia.co.zw")) { setErr("Email must be @alphamedia.co.zw"); return; }

    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users`, {
        method: "POST",
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim().toLowerCase(), role: form.role, permissions: form.permissions }),
      });
      const data = await res.json();
      if (res.status === 401) throw new Error("Session expired — please log out and sign in again");
      if (!res.ok) throw new Error(data.error || "Failed to create user");
      setSuccess(`✓ User created. Temp password logged to server console.`);
      setTimeout(onSaved, 1200);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  const showPermissions = form.role === "viewer" || form.role === "editor";
  const allRoles = isSuperAdmin ? [...ROLES, { value: "super_admin", label: "Super Admin", desc: "Full access, can manage all users including other admins" }] : ROLES;

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Add New User</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>Account will be created with a temporary password.</p>
        </div>
        <button type="button" onClick={onCancel}
          style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "7px 16px", color: t.textSub, fontSize: 12, cursor: "pointer" }}>
          Cancel
        </button>
      </div>

      {err     && <div style={{ background: "#DC262611", border: "1px solid #DC262633", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>⚠️ {err}</div>}
      {success && <div style={{ background: "#22c55e11", border: "1px solid #22c55e33", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#22c55e" }}>{success}</div>}

      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {[{ key: "name", label: "Full Name", placeholder: "Jane Doe", type: "text" },
            { key: "email", label: "Email", placeholder: "jane@alphamedia.co.zw", type: "email" }].map(f => (
            <div key={f.key}>
              <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, fontWeight: 700 }}>{f.label}</label>
              <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
          ))}
        </div>

        <div style={{ marginBottom: showPermissions ? 24 : 0 }}>
          <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, fontWeight: 700 }}>Role</label>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${allRoles.length}, 1fr)`, gap: 10 }}>
            {allRoles.map(r => (
              <button type="button" key={r.value} onClick={() => handleRoleChange(r.value)}
                style={{
                  background: form.role === r.value ? "#2563EB18" : t.surface2,
                  border: `1px solid ${form.role === r.value ? "#2563EB66" : t.border}`,
                  borderRadius: 10, padding: "10px 12px", cursor: "pointer", textAlign: "left",
                }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: form.role === r.value ? "#60a5fa" : t.text, marginBottom: 3 }}>{r.label}</div>
                <div style={{ fontSize: 10, color: t.textFaint, lineHeight: 1.4 }}>{r.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {showPermissions && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>Section Access</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => setForm(f => ({ ...f, permissions: allTrue() }))}
                  style={{ background: "none", border: "none", color: "#2563EB", fontSize: 11, cursor: "pointer", padding: 0 }}>Grant All</button>
                <button type="button" onClick={() => setForm(f => ({ ...f, permissions: allFalse() }))}
                  style={{ background: "none", border: "none", color: t.textMuted, fontSize: 11, cursor: "pointer", padding: 0 }}>Revoke All</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
              {SECTIONS.map(s => (
                <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: t.surface2, border: `1px solid ${form.permissions[s.key] ? "#2563EB44" : t.border}`, borderRadius: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.permissions[s.key]} onChange={() => togglePerm(s.key)}
                    style={{ width: 14, height: 14, accentColor: "#2563EB", cursor: "pointer" }} />
                  <span style={{ fontSize: 12, color: form.permissions[s.key] ? t.text : t.textMuted, fontWeight: form.permissions[s.key] ? 700 : 400 }}>{s.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={saving}
          style={{ background: saving ? t.surface3 : "#2563EB", border: "none", borderRadius: 10, padding: "11px 24px", color: "#fff", fontSize: 13, fontWeight: 800, cursor: saving ? "default" : "pointer", fontFamily: "Georgia, serif" }}>
          {saving ? "Creating…" : "Create User"}
        </button>
        <button type="button" onClick={onCancel}
          style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 10, padding: "11px 24px", color: t.textSub, fontSize: 13, cursor: "pointer", fontFamily: "Georgia, serif" }}>
          Cancel
        </button>
      </div>
    </form>
  );
}
